/* eslint-disable react/prop-types */
import React, { useCallback, useMemo, useState } from 'react';
import {
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormGroup,
	InputLabel,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	SelectChangeEvent,
	TextField,
	Typography,
} from '@mui/material';
import {
	EmissionRequestAddOnTypeEnum,
	EmissionRequestPetLocationEnum,
	EmissionRequestSpecialServiceTypeEnum,
	EmissionsRequestAddOnDetail,
	EmissionsRequestTypeEnum,
	IEmissionRequestTicketDetailItem,
	IEmissionsRequest,
	IEmissionsRequestAddOnDetailCabinBaggage,
	IEmissionsRequestAddOnDetailEntertainment,
	IEmissionsRequestAddOnDetailGeneral,
	IEmissionsRequestAddOnDetailHoldBaggage,
	IEmissionsRequestAddOnDetailMeals,
	IEmissionsRequestAddOnDetailPet,
	IEmissionsRequestAddOnDetailSeat,
	IEmissionsRequestAddOnDetailSpecialBaggage,
	IEmissionsRequestAddOnDetailUnaccompaniedMinor,
	IEmissionsRequestServiceDetail,
	IEmissionsRequestTicketDetailContainer,
	IPassenger,
	IServices,
	OperationalRequestCategoryEnum,
	TicketScopeEnum,
	TypeServiceEnum,
} from 'features/salesOrder/types';
import { ServiceSelector } from './ServiceSelector';
import { FileUploader } from './FileUploader';
import { createOperationalRequest } from 'features/salesOrder/services';
import { extractErrorMessage } from 'features/quotation/helpers';
import { ShowAlertState } from 'features/common/types';
import { fileToBase64Attachment } from '../../helpers';
import { EMISSIONS_REQUEST_TYPE_LABELS } from '../../../../../features/salesOrder/constants';

interface EmissionsRequestModalProps {
	open: boolean;
	onClose: () => void;
	services: IServices[];
	passengers: IPassenger[];
	saleOrderId: string;
	onSuccess: (caseId: number) => void;
	onSetAlert: (alert: ShowAlertState) => void;
}

const emissionsRequestTypes: {
	value: EmissionsRequestTypeEnum;
	label: string;
}[] = Object.entries(EMISSIONS_REQUEST_TYPE_LABELS).map(([value, label]) => ({
	value: value as EmissionsRequestTypeEnum,
	label,
}));

// Opciones para servicios especiales

const specialServicesOptions = [
	{
		value: EmissionRequestSpecialServiceTypeEnum.WheelchairAirport,
		label: 'Silla de ruedas (en aeropuerto o hasta la puerta del avión)',
	},
	{
		value: EmissionRequestSpecialServiceTypeEnum.UnaccompaniedMinor,
		label: 'Menor acompañado (servicio de asistencia para menores viajando solos)',
	},
	{
		value: EmissionRequestSpecialServiceTypeEnum.SpecialMeal,
		label: 'Comida especial (para infantes, vegetariana, sin gluten, etc.)',
	},
	{
		value: EmissionRequestSpecialServiceTypeEnum.OxygenOnboard,
		label: 'Oxígeno a bordo (cuando lo permite la aerolínea)',
	},
	{
		value: EmissionRequestSpecialServiceTypeEnum.PriorityBoarding,
		label: 'Embarque prioritario por movilidad reducida',
	},
	{
		value: EmissionRequestSpecialServiceTypeEnum.DisabilityAssistance,
		label: 'Asistencia para pasajeros con discapacidad visual o auditiva',
	},
];

// Opciones para tipos de adicionales
const addOnTypes = [
	{ value: EmissionRequestAddOnTypeEnum.Seat, label: '✈️ Asientos' },
	{ value: EmissionRequestAddOnTypeEnum.CabinBaggage, label: '🧳 Equipaje en cabina' },
	{ value: EmissionRequestAddOnTypeEnum.HoldBaggage, label: '🧳 Equipaje en bodega' },
	{ value: EmissionRequestAddOnTypeEnum.Pet, label: '🐾 Mascota' },
	{ value: EmissionRequestAddOnTypeEnum.Entertainment, label: '🎧 Entretenimiento a bordo' },
	{ value: EmissionRequestAddOnTypeEnum.Meals, label: '🍽️ Comidas' },
	{ value: EmissionRequestAddOnTypeEnum.SpecialBaggage, label: '🎿 Equipajes especiales' },
	{ value: EmissionRequestAddOnTypeEnum.UnaccompaniedMinor, label: '👶 Menor acompañado' },
	{ value: EmissionRequestAddOnTypeEnum.General, label: '🕵️ General' },
];

const buildAddOnDetails = (
	addOnType?: EmissionRequestAddOnTypeEnum,
	raw?: {
		seatNumber?: string;
		seatLocation?: string;
		cabinBaggageQuantity?: string;
		cabinBaggageDimensions?: string;
		holdBaggageQuantity?: string;
		holdBaggageWeight?: string;
		petBreed?: string;
		petWeight?: string;
		petCarrierWeight?: string;
		petCarrierDimensions?: string;
		petLocation?: EmissionRequestPetLocationEnum;
		entertainmentScope?: string;
		entertainmentPreference?: string;
		mealType?: string;
		specialBaggageType?: string;
		specialBaggageDimensions?: string;
		specialBaggageWeight?: string;
		minorAge?: string;
		minorSegments?: string;
		minorTravelingAlone?: boolean;
		generalInfo?: string;
	},
): EmissionsRequestAddOnDetail | undefined => {
	if (!addOnType) return undefined;
	if (!raw) return {}; // se envia vacio porque el backend lo necesita que si se envia el addOnType se envie el objeto del detalle aunque sea vacio

	switch (addOnType) {
		case EmissionRequestAddOnTypeEnum.Seat: {
			const detail: IEmissionsRequestAddOnDetailSeat = {
				seatNumber: raw.seatNumber,
				seatLocation: raw.seatLocation,
			};
			return detail;
		}

		case EmissionRequestAddOnTypeEnum.CabinBaggage: {
			const detail: IEmissionsRequestAddOnDetailCabinBaggage = {
				cabinBaggageQuantity: raw.cabinBaggageQuantity,
				cabinBaggageDimensions: raw.cabinBaggageDimensions,
			};
			return detail;
		}

		case EmissionRequestAddOnTypeEnum.HoldBaggage: {
			const detail: IEmissionsRequestAddOnDetailHoldBaggage = {
				holdBaggageQuantity: raw.holdBaggageQuantity,
				holdBaggageWeight: raw.holdBaggageWeight,
			};
			return detail;
		}

		case EmissionRequestAddOnTypeEnum.Pet: {
			const detail: IEmissionsRequestAddOnDetailPet = {
				petBreed: raw.petBreed,
				petWeight: raw.petWeight,
				petCarrierWeight: raw.petCarrierWeight,
				petCarrierDimensions: raw.petCarrierDimensions,
				petLocation: raw.petLocation,
			};
			return detail;
		}

		case EmissionRequestAddOnTypeEnum.Entertainment: {
			const detail: IEmissionsRequestAddOnDetailEntertainment = {
				entertainmentScope: raw.entertainmentScope,
				entertainmentPreference: raw.entertainmentPreference,
			};
			return detail;
		}

		case EmissionRequestAddOnTypeEnum.Meals: {
			const detail: IEmissionsRequestAddOnDetailMeals = {
				mealType: raw.mealType,
			};
			return detail;
		}

		case EmissionRequestAddOnTypeEnum.SpecialBaggage: {
			const detail: IEmissionsRequestAddOnDetailSpecialBaggage = {
				specialBaggageType: raw.specialBaggageType,
				specialBaggageDimensions: raw.specialBaggageDimensions,
				specialBaggageWeight: raw.specialBaggageWeight,
			};
			return detail;
		}

		case EmissionRequestAddOnTypeEnum.UnaccompaniedMinor: {
			const detail: IEmissionsRequestAddOnDetailUnaccompaniedMinor = {
				minorAge: raw.minorAge,
				minorSegments: raw.minorSegments,
				minorTravelingAlone: raw.minorTravelingAlone ? 'Sí' : 'No',
			};
			return detail;
		}

		case EmissionRequestAddOnTypeEnum.General: {
			const detail: IEmissionsRequestAddOnDetailGeneral = {
				generalInfo: raw.generalInfo,
			};
			return detail;
		}

		default:
			return undefined;
	}
};

export const EmissionsRequestModal: React.FC<EmissionsRequestModalProps> = ({
	open,
	onClose,
	services,
	passengers,
	saleOrderId,
	onSuccess,
	onSetAlert,
}) => {
	const [requestType, setRequestType] = useState<EmissionsRequestTypeEnum>(EmissionsRequestTypeEnum.VoidTicket);
	const [selectedServiceIds, setSelectedServiceIds] = useState<string[]>([]);
	const [ticketScope, setTicketScope] = useState<TicketScopeEnum>(TicketScopeEnum.AllTickets);
	const [specificTickets, setSpecificTickets] = useState<string[]>([]);
	const [observations, setObservations] = useState('');
	const [attachments, setAttachments] = useState<File[]>([]);
	const [loading, setLoading] = useState(false);

	// Estructura para almacenar detalles por ticket (para servicios especiales, corrección de nombres, cotización de adicionales)
	const [ticketDetails, setTicketDetails] = useState<{
		[serviceId: string]: {
			[ticket: string]: {
				specialService?: EmissionRequestSpecialServiceTypeEnum;
				currentName?: string;
				correctName?: string;
				addOnType?: EmissionRequestAddOnTypeEnum;
				addOnDetails?: {
					seatNumber?: string;
					seatLocation?: string;
					cabinBaggageQuantity?: string;
					cabinBaggageDimensions?: string;
					holdBaggageQuantity?: string;
					holdBaggageWeight?: string;
					petBreed?: string;
					petWeight?: string;
					petCarrierWeight?: string;
					petCarrierDimensions?: string;
					petLocation?: EmissionRequestPetLocationEnum;
					entertainmentScope?: string;
					entertainmentPreference?: string;
					mealType?: string;
					specialBaggageType?: string;
					specialBaggageDimensions?: string;
					specialBaggageWeight?: string;
					minorAge?: string;
					minorSegments?: string;
					minorTravelingAlone?: boolean;
					generalInfo?: string;
				};
			};
		};
	}>({});

	// Estructura para almacenar detalles por servicio (para cambios invol/voluntarios)
	const [serviceDetails, setServiceDetails] = useState<{
		[serviceId: string]: {
			gdsFormat?: string;
		};
	}>({});

	// Extraer tickets disponibles por servicio (no mezclados) para Flight y FlightAncillary
	const getTicketsByService = useCallback(
		(serviceId: string) => {
			const service = services.find((s) => s.keyId === serviceId);
			const tickets: Array<{ ticket: string; passengerId: string; passengerName: string }> = [];

			if (
				service &&
				(service.serviceType === TypeServiceEnum.Flight || service.serviceType === TypeServiceEnum.FlightAncillary)
			) {
				if (service.serviceType === TypeServiceEnum.Flight) {
					const groups = (service as any).passengersGroupByPassengerType || [];
					groups.forEach((group: any) => {
						if (group.passengersTickets && Array.isArray(group.passengersTickets)) {
							group.passengersTickets.forEach((ticketData: any) => {
								if (ticketData.ticket && ticketData.passengerId) {
									const passenger = passengers.find((p) => p.keyId === ticketData.passengerId);
									const passengerName = passenger
										? `${passenger.name} ${passenger.lastName}`
										: 'Pasajero no encontrado';
									tickets.push({
										ticket: ticketData.ticket,
										passengerId: ticketData.passengerId,
										passengerName,
									});
								}
							});
						}
					});
				} else if (service.serviceType === TypeServiceEnum.FlightAncillary) {
					const ticketsData = (service as any).passengersTickets || [];
					ticketsData.forEach((ticketData: any) => {
						if (ticketData.ticket && ticketData.passengerId) {
							const passenger = passengers.find((p) => p.keyId === ticketData.passengerId);
							const passengerName = passenger ? `${passenger.name} ${passenger.lastName}` : 'Pasajero no encontrado';
							tickets.push({
								ticket: ticketData.ticket,
								passengerId: ticketData.passengerId,
								passengerName,
							});
						}
					});
				}
			}
			return tickets;
		},
		[services, passengers],
	);

	// Agrupar tickets por servicio para mostrar en la UI
	const ticketsByService = useMemo(() => {
		return selectedServiceIds.map((serviceId) => {
			const service = services.find((s) => s.keyId === serviceId);
			const tickets = getTicketsByService(serviceId);
			return {
				serviceId,
				service,
				tickets,
			};
		});
	}, [selectedServiceIds, services, getTicketsByService]);

	// Obtener todos los tickets disponibles (para validación)
	const availableTickets = useMemo(() => {
		const allTickets: Array<{ ticket: string; passengerId: string; passengerName: string }> = [];
		ticketsByService.forEach(({ tickets }) => {
			allTickets.push(...tickets);
		});
		// Eliminar duplicados basándose en el ticket
		return Array.from(new Map(allTickets.map((t) => [t.ticket, t])).values());
	}, [ticketsByService]);

	const handleRequestTypeChange = (event: SelectChangeEvent) => {
		const newRequestType = event.target.value as EmissionsRequestTypeEnum;
		setRequestType(newRequestType);
		// Limpiar detalles cuando cambia el tipo de solicitud
		setTicketDetails({});
		setServiceDetails({});
		// Para servicios especiales, corrección de nombres y cotización de adicionales, forzar el alcance a "eTickets específicos"
		if (
			newRequestType === EmissionsRequestTypeEnum.SpecialServices ||
			newRequestType === EmissionsRequestTypeEnum.NameCorrection ||
			newRequestType === EmissionsRequestTypeEnum.AddOnsQuotation
		) {
			setTicketScope(TicketScopeEnum.SpecificTickets);
		}
	};

	const handleTicketScopeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const newScope = event.target.value as TicketScopeEnum;
		setTicketScope(newScope);
		if (newScope === TicketScopeEnum.AllTickets) {
			setSpecificTickets([]);
		}
	};

	const handleTicketToggle = (ticket: string, serviceId: string) => {
		if (specificTickets.includes(ticket)) {
			setSpecificTickets(specificTickets.filter((t) => t !== ticket));
			// Limpiar detalles del ticket cuando se deselecciona
			setTicketDetails((prev) => {
				const newDetails = { ...prev };
				if (newDetails[serviceId]) {
					// eslint-disable-next-line @typescript-eslint/no-unused-vars
					const { [ticket]: _removed, ...rest } = newDetails[serviceId];
					newDetails[serviceId] = rest;
					if (Object.keys(newDetails[serviceId]).length === 0) {
						delete newDetails[serviceId];
					}
				}
				return newDetails;
			});
		} else {
			setSpecificTickets([...specificTickets, ticket]);
		}
	};

	// Funciones para actualizar detalles de tickets
	const updateTicketDetail = (serviceId: string, ticket: string, field: string, value: any) => {
		setTicketDetails((prev) => ({
			...prev,
			[serviceId]: {
				...(prev[serviceId] || {}),
				[ticket]: {
					...(prev[serviceId]?.[ticket] || {}),
					[field]: value,
				},
			},
		}));
	};

	const updateTicketAddOnDetail = (serviceId: string, ticket: string, field: string, value: any) => {
		setTicketDetails((prev) => ({
			...prev,
			[serviceId]: {
				...(prev[serviceId] || {}),
				[ticket]: {
					...(prev[serviceId]?.[ticket] || {}),
					addOnDetails: {
						...(prev[serviceId]?.[ticket]?.addOnDetails || {}),
						[field]: value,
					},
				},
			},
		}));
	};

	// Función para actualizar detalles de servicio (GDS format)
	const updateServiceDetail = (serviceId: string, field: string, value: any) => {
		setServiceDetails((prev) => ({
			...prev,
			[serviceId]: {
				...(prev[serviceId] || {}),
				[field]: value,
			},
		}));
	};

	const handleSubmit = async () => {
		if (selectedServiceIds.length === 0) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'Debe seleccionar al menos un servicio',
			});
			return;
		}

		if (
			(ticketScope === TicketScopeEnum.SpecificTickets ||
				requestType === EmissionsRequestTypeEnum.SpecialServices ||
				requestType === EmissionsRequestTypeEnum.NameCorrection ||
				requestType === EmissionsRequestTypeEnum.AddOnsQuotation) &&
			specificTickets.length === 0
		) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'Debe seleccionar al menos un ticket específico',
			});
			return;
		}

		// Validación específica para servicios especiales: cada ticket debe tener un servicio especial seleccionado
		if (requestType === EmissionsRequestTypeEnum.SpecialServices) {
			const ticketsWithoutService: string[] = [];
			specificTickets.forEach((ticket) => {
				// Verificar si el ticket tiene un servicio especial en alguno de los servicios seleccionados
				let hasService = false;
				for (const serviceId of selectedServiceIds) {
					const ticketData = ticketDetails[serviceId]?.[ticket];
					if (ticketData?.specialService) {
						hasService = true;
						break;
					}
				}
				if (!hasService) {
					ticketsWithoutService.push(ticket);
				}
			});

			if (ticketsWithoutService.length > 0) {
				onSetAlert({
					show: true,
					severity: 'error',
					message: `Debe seleccionar un servicio especial para cada ticket seleccionado. Faltan ${ticketsWithoutService.length} ticket(s) sin servicio especial.`,
				});
				return;
			}
		}

		// Validación específica para cotizacion de adicionales: cada ticket debe tener un adicional seleccionado
		if (requestType === EmissionsRequestTypeEnum.AddOnsQuotation) {
			const ticketsAddOnType: string[] = [];
			specificTickets.forEach((ticket) => {
				// Verificar si el ticket tiene un servicio especial en alguno de los servicios seleccionados
				let hasAddOnType = false;
				for (const serviceId of selectedServiceIds) {
					const ticketData = ticketDetails[serviceId]?.[ticket];
					if (ticketData?.addOnType) {
						hasAddOnType = true;
						break;
					}
				}
				if (!hasAddOnType) {
					ticketsAddOnType.push(ticket);
				}
			});

			if (ticketsAddOnType.length > 0) {
				onSetAlert({
					show: true,
					severity: 'error',
					message: `Debe seleccionar un tipo de adicional para cada ticket seleccionado. Faltan ${ticketsAddOnType.length} ticket(s) sin tipo de adicional.`,
				});
				return;
			}
		}

		// Validación específica para corrección de nombres:
		// cada ticket debe tener currentName y correctName no vacíos
		if (requestType === EmissionsRequestTypeEnum.NameCorrection) {
			const invalidTickets: string[] = [];

			for (const ticket of specificTickets) {
				let isValid = false;

				for (const serviceId of selectedServiceIds) {
					const ticketData = ticketDetails[serviceId]?.[ticket];

					if (ticketData?.currentName?.trim() && ticketData?.correctName?.trim()) {
						isValid = true;
						break;
					}
				}

				if (!isValid) {
					invalidTickets.push(ticket);
				}
			}

			if (invalidTickets.length > 0) {
				onSetAlert({
					show: true,
					severity: 'error',
					message: `Para corrección de nombres, cada ticket debe tener Nombre actual y Nombre correcto. Faltan ${invalidTickets.length} ticket(s).`,
				});
				return;
			}
		}

		// Validación para cambios invol/voluntarios: cada servicio debe tener el formato GDS completado
		if (
			requestType === EmissionsRequestTypeEnum.InvoluntaryChangeConfirmation ||
			requestType === EmissionsRequestTypeEnum.VoluntaryChangeQuotation
		) {
			const servicesWithoutGDS: string[] = [];
			selectedServiceIds.forEach((serviceId) => {
				const gdsFormat = serviceDetails[serviceId]?.gdsFormat;
				if (!gdsFormat || gdsFormat.trim() === '') {
					const service = services.find((s) => s.keyId === serviceId);
					const serviceName = service ? (service as any).reference || `Servicio ${serviceId}` : `Servicio ${serviceId}`;
					servicesWithoutGDS.push(serviceName);
				}
			});

			if (servicesWithoutGDS.length > 0) {
				onSetAlert({
					show: true,
					severity: 'error',
					message: `Debe completar el campo "Servicio solicitado en formato GDS" para todos los servicios seleccionados. Faltan: ${servicesWithoutGDS.join(
						', ',
					)}`,
				});
				return;
			}
		}

		try {
			setLoading(true);
			const files = Array.from(attachments.length > 0 ? attachments : []);
			const filesToAttachments = await Promise.all(files.map(fileToBase64Attachment));
			const requestData: IEmissionsRequest = {
				requestCategory: OperationalRequestCategoryEnum.Emissions,
				requestType,
				saleOrderId,
				serviceIds: selectedServiceIds,
				ticketScope: ticketScope,
				specificTickets: ticketScope === TicketScopeEnum.SpecificTickets ? specificTickets : undefined,
				observations: observations || undefined,
				attachments: attachments.length > 0 ? filesToAttachments : undefined,
			};

			// Incluir detalles adicionales según el tipo de solicitud
			if (Object.keys(ticketDetails).length > 0) {
				const ticketsDetailByServiceToSend: IEmissionsRequestTicketDetailContainer[] = Object.entries(
					ticketDetails,
				).map(([serviceId, ticketsByNumber]) => ({
					serviceId,
					tickets: Object.entries(ticketsByNumber).map(
						([ticket, details]): IEmissionRequestTicketDetailItem => ({
							ticket,
							specialService: details.specialService,
							currentName: details.currentName,
							correctName: details.correctName,
							addOnType: details.addOnType,
							addOnDetails: buildAddOnDetails(details.addOnType, details.addOnDetails),
						}),
					),
				}));
				requestData.ticketsDetailByService = ticketsDetailByServiceToSend;
			}

			if (Object.keys(serviceDetails).length > 0) {
				const servicesDetailToSend: IEmissionsRequestServiceDetail[] = Object.entries(serviceDetails).map(
					([serviceId, { gdsFormat }]) => ({
						serviceId,
						gdsFormat: gdsFormat || '', //ya se valido arriba deberia de tener valor
					}),
				);
				requestData.serviceDetails = servicesDetailToSend;
			}

			const response = await createOperationalRequest(requestData);
			onSuccess(response.data.caseId);
			onSetAlert({
				show: true,
				severity: 'success',
				message: `Solicitud creada exitosamente. Caso #${response.data.caseId} creado en el CRM.`,
			});

			// Reset form
			setSelectedServiceIds([]);
			setTicketScope(TicketScopeEnum.AllTickets);
			setSpecificTickets([]);
			setObservations('');
			setAttachments([]);
			setTicketDetails({});
			setServiceDetails({});
			onClose();
		} catch (error) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: extractErrorMessage(error, 'Ocurrió un error al crear la solicitud'),
			});
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (!loading) {
			setSelectedServiceIds([]);
			setTicketScope(TicketScopeEnum.AllTickets);
			setSpecificTickets([]);
			setObservations('');
			setAttachments([]);
			setTicketDetails({});
			setServiceDetails({});
			onClose();
		}
	};

	// Filtrar servicios: solo Flight y FlightAncillary
	const filterServices = (service: IServices) => {
		return service.serviceType === TypeServiceEnum.Flight || service.serviceType === TypeServiceEnum.FlightAncillary;
	};

	// Renderizar campos específicos según el tipo de solicitud
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const renderTicketSpecificFields = (serviceId: string, ticket: string, _passengerName: string) => {
		const ticketData = ticketDetails[serviceId]?.[ticket] || {};

		if (requestType === EmissionsRequestTypeEnum.SpecialServices) {
			return (
				<Box sx={{ ml: 4, mt: 1, mb: 2 }}>
					<FormControl fullWidth size="small" required={true}>
						<InputLabel>Servicio especial</InputLabel>
						<Select
							value={ticketData.specialService || ''}
							onChange={(e) => updateTicketDetail(serviceId, ticket, 'specialService', e.target.value)}
							disabled={loading}
							label="Servicio especial"
						>
							{specialServicesOptions.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</Box>
			);
		}

		if (requestType === EmissionsRequestTypeEnum.NameCorrection) {
			return (
				<Box sx={{ ml: 4, mt: 1, mb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
					<TextField
						fullWidth
						required={true}
						size="small"
						label="Nombre actual"
						placeholder="Ejemplo: RLODRIGUEZ/VALENTIN"
						value={ticketData.currentName || ''}
						onChange={(e) => updateTicketDetail(serviceId, ticket, 'currentName', e.target.value)}
						disabled={loading}
					/>
					<TextField
						fullWidth
						required={true}
						size="small"
						label="Nombre correcto"
						placeholder="Ejemplo: RLODRIGUEZ/VALENTINO"
						value={ticketData.correctName || ''}
						onChange={(e) => updateTicketDetail(serviceId, ticket, 'correctName', e.target.value)}
						disabled={loading}
					/>
				</Box>
			);
		}

		if (requestType === EmissionsRequestTypeEnum.AddOnsQuotation) {
			const addOnDetails = ticketData.addOnDetails || {};
			return (
				<Box sx={{ ml: 4, mt: 1, mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
					<FormControl fullWidth size="small" required={true}>
						<InputLabel>Tipo de adicional</InputLabel>
						<Select
							value={ticketData.addOnType || ''}
							onChange={(e) => updateTicketDetail(serviceId, ticket, 'addOnType', e.target.value)}
							disabled={loading}
							label="Tipo de adicional"
						>
							{addOnTypes.map((option) => (
								<MenuItem key={option.value} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					{/* Campos según el tipo de adicional seleccionado */}
					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.Seat && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<TextField
								fullWidth
								size="small"
								label="Número de asiento"
								placeholder="Ejemplo: 12A"
								value={addOnDetails.seatNumber || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'seatNumber', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Ubicación deseada"
								placeholder="Ejemplo: ventana, pasillo, fila de emergencia, primera fila"
								value={addOnDetails.seatLocation || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'seatLocation', e.target.value)}
								disabled={loading}
							/>
						</Box>
					)}

					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.CabinBaggage && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<TextField
								fullWidth
								size="small"
								label="Cantidad"
								type="number"
								value={addOnDetails.cabinBaggageQuantity || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'cabinBaggageQuantity', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Medidas (si son relevantes)"
								placeholder="Ejemplo: 55x40x20 cm"
								value={addOnDetails.cabinBaggageDimensions || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'cabinBaggageDimensions', e.target.value)}
								disabled={loading}
							/>
						</Box>
					)}

					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.HoldBaggage && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<TextField
								fullWidth
								size="small"
								label="Cantidad"
								type="number"
								value={addOnDetails.holdBaggageQuantity || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'holdBaggageQuantity', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Peso (kg)"
								type="number"
								value={addOnDetails.holdBaggageWeight || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'holdBaggageWeight', e.target.value)}
								disabled={loading}
							/>
						</Box>
					)}

					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.Pet && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<TextField
								fullWidth
								size="small"
								label="Raza"
								value={addOnDetails.petBreed || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'petBreed', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Peso de la mascota (kg)"
								type="number"
								value={addOnDetails.petWeight || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'petWeight', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Peso del canil (kg)"
								type="number"
								value={addOnDetails.petCarrierWeight || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'petCarrierWeight', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Medidas del canil"
								placeholder="Ejemplo: 50x30x30 cm"
								value={addOnDetails.petCarrierDimensions || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'petCarrierDimensions', e.target.value)}
								disabled={loading}
							/>
							<FormControl fullWidth size="small">
								<InputLabel>Ubicación</InputLabel>
								<Select
									value={addOnDetails.petLocation || ''}
									onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'petLocation', e.target.value)}
									disabled={loading}
									label="Ubicación"
								>
									<MenuItem value={EmissionRequestPetLocationEnum.Cabin}>Cabina</MenuItem>
									<MenuItem value={EmissionRequestPetLocationEnum.Hold}>Bodega</MenuItem>
								</Select>
							</FormControl>
						</Box>
					)}

					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.Entertainment && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<TextField
								fullWidth
								size="small"
								label="Alcance"
								placeholder="Ejemplo: vuelo completo, tramo específico"
								value={addOnDetails.entertainmentScope || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'entertainmentScope', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Preferencia específica"
								value={addOnDetails.entertainmentPreference || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'entertainmentPreference', e.target.value)}
								disabled={loading}
							/>
						</Box>
					)}

					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.Meals && (
						<TextField
							fullWidth
							size="small"
							label="Tipo de comida"
							placeholder="Ejemplo: vegetariana, sin gluten, sin lactosa, infantil, halal"
							value={addOnDetails.mealType || ''}
							onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'mealType', e.target.value)}
							disabled={loading}
						/>
					)}

					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.SpecialBaggage && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<TextField
								fullWidth
								size="small"
								label="Tipo de equipaje"
								placeholder="Ejemplo: deportivo, médico, musical"
								value={addOnDetails.specialBaggageType || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'specialBaggageType', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Dimensiones aproximadas"
								placeholder="Ejemplo: 150x50x30 cm"
								value={addOnDetails.specialBaggageDimensions || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'specialBaggageDimensions', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Peso estimado (kg)"
								type="number"
								value={addOnDetails.specialBaggageWeight || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'specialBaggageWeight', e.target.value)}
								disabled={loading}
							/>
						</Box>
					)}

					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.UnaccompaniedMinor && (
						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
							<TextField
								fullWidth
								size="small"
								label="Edad del menor"
								type="number"
								value={addOnDetails.minorAge || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'minorAge', e.target.value)}
								disabled={loading}
							/>
							<TextField
								fullWidth
								size="small"
								label="Tramos donde requiere asistencia"
								value={addOnDetails.minorSegments || ''}
								onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'minorSegments', e.target.value)}
								disabled={loading}
							/>
							<FormControlLabel
								control={
									<Checkbox
										checked={addOnDetails.minorTravelingAlone || false}
										onChange={(e) =>
											updateTicketAddOnDetail(serviceId, ticket, 'minorTravelingAlone', e.target.checked)
										}
										disabled={loading}
									/>
								}
								label="Viaja solo"
							/>
						</Box>
					)}

					{ticketData.addOnType === EmissionRequestAddOnTypeEnum.General && (
						<TextField
							fullWidth
							size="small"
							multiline
							rows={3}
							label="Información general"
							placeholder="Indicar si ya se verificó información en alguna fuente (web de la aerolínea, GDS, etc.)"
							value={addOnDetails.generalInfo || ''}
							onChange={(e) => updateTicketAddOnDetail(serviceId, ticket, 'generalInfo', e.target.value)}
							disabled={loading}
						/>
					)}
				</Box>
			);
		}

		return null;
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>Solicitud al equipo de emisiones</DialogTitle>
			<DialogContent>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
					<FormControl fullWidth>
						<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
							Tipo de solicitud *
						</Typography>
						<Select value={requestType} onChange={handleRequestTypeChange} disabled={loading}>
							{emissionsRequestTypes.map((type) => (
								<MenuItem key={type.value} value={type.value}>
									{type.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>

					<ServiceSelector
						services={services}
						selectedServiceIds={selectedServiceIds}
						onChange={setSelectedServiceIds}
						filterByType={filterServices}
						disabled={loading}
					/>

					{/* Para servicios especiales, corrección de nombres y cotización de adicionales, no mostrar el selector de alcance */}
					{requestType !== EmissionsRequestTypeEnum.SpecialServices &&
						requestType !== EmissionsRequestTypeEnum.NameCorrection &&
						requestType !== EmissionsRequestTypeEnum.AddOnsQuotation && (
							<FormControl component="fieldset">
								<Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
									Alcance de la solicitud *
								</Typography>
								<RadioGroup value={ticketScope} onChange={handleTicketScopeChange}>
									<FormControlLabel
										value={TicketScopeEnum.AllTickets}
										control={<Radio />}
										label="Totalidad de tickets que contiene el servicio"
										disabled={loading}
									/>
									<FormControlLabel
										value={TicketScopeEnum.SpecificTickets}
										control={<Radio />}
										label="eTickets específicamente"
										disabled={loading}
									/>
								</RadioGroup>
							</FormControl>
						)}

					{(ticketScope === TicketScopeEnum.SpecificTickets ||
						requestType === EmissionsRequestTypeEnum.SpecialServices ||
						requestType === EmissionsRequestTypeEnum.NameCorrection ||
						requestType === EmissionsRequestTypeEnum.AddOnsQuotation) && (
						<Box>
							<Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
								Seleccionar eTickets específicos *
							</Typography>
							{ticketsByService.map(({ serviceId, service, tickets }) => {
								if (tickets.length === 0) return null;

								return (
									<Box
										key={serviceId}
										sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
									>
										<Box sx={{ mb: 2 }}>
											<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
												{service ? (service as any).reference || `Servicio ${serviceId}` : `Servicio ${serviceId}`}
											</Typography>
											{service?.serviceType && (
												<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
													Tipo:{' '}
													{service.serviceType === TypeServiceEnum.Flight
														? 'Vuelo'
														: service.serviceType === TypeServiceEnum.FlightAncillary
														? 'Adicional de vuelo'
														: 'Otro'}
												</Typography>
											)}
										</Box>
										<FormGroup>
											{tickets.map((ticketInfo) => {
												const isSelected = specificTickets.includes(ticketInfo.ticket);
												return (
													<Box key={ticketInfo.ticket} sx={{ mb: 2 }}>
														<FormControlLabel
															control={
																<Checkbox
																	checked={isSelected}
																	onChange={() => handleTicketToggle(ticketInfo.ticket, serviceId)}
																	disabled={loading}
																/>
															}
															label={
																<Box>
																	<Typography variant="body2" component="span" sx={{ fontWeight: 'bold' }}>
																		{ticketInfo.ticket}
																	</Typography>
																	<Typography variant="body2" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
																		- {ticketInfo.passengerName}
																	</Typography>
																</Box>
															}
														/>
														{isSelected &&
															renderTicketSpecificFields(serviceId, ticketInfo.ticket, ticketInfo.passengerName)}
													</Box>
												);
											})}
										</FormGroup>
									</Box>
								);
							})}
							{availableTickets.length === 0 && selectedServiceIds.length > 0 && (
								<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
									No se encontraron tickets en los servicios seleccionados. Puede continuar con la solicitud.
								</Typography>
							)}
						</Box>
					)}

					{/* Campos específicos por ticket cuando ticketScope es "AllTickets" */}
					{ticketScope === TicketScopeEnum.AllTickets &&
						requestType === EmissionsRequestTypeEnum.SpecialServices &&
						selectedServiceIds.length > 0 && (
							<Box>
								<Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
									Información por ticket *
								</Typography>
								{ticketsByService.map(({ serviceId, service, tickets }) => {
									if (tickets.length === 0) return null;

									return (
										<Box
											key={serviceId}
											sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
										>
											<Box sx={{ mb: 2 }}>
												<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
													{service ? (service as any).reference || `Servicio ${serviceId}` : `Servicio ${serviceId}`}
												</Typography>
											</Box>
											{tickets.map((ticketInfo) => (
												<Box key={ticketInfo.ticket} sx={{ mb: 2 }}>
													<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
														{ticketInfo.ticket} - {ticketInfo.passengerName}
													</Typography>
													{renderTicketSpecificFields(serviceId, ticketInfo.ticket, ticketInfo.passengerName)}
												</Box>
											))}
										</Box>
									);
								})}
							</Box>
						)}

					{/* Campos específicos por servicio para cambios invol/voluntarios */}
					{(requestType === EmissionsRequestTypeEnum.InvoluntaryChangeConfirmation ||
						requestType === EmissionsRequestTypeEnum.VoluntaryChangeQuotation) &&
						selectedServiceIds.length > 0 && (
							<Box>
								<Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold' }}>
									Información del servicio en formato GDS *
								</Typography>
								{ticketsByService.map(({ serviceId, service }) => (
									<Box
										key={serviceId}
										sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
									>
										<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
											{service ? (service as any).reference || `Servicio ${serviceId}` : `Servicio ${serviceId}`}
										</Typography>
										<TextField
											fullWidth
											multiline
											rows={6}
											label="Servicio solicitado en formato GDS"
											placeholder="Ejemplo: RP/MVDUY218N/&#10;&#10;  1  ZP 845 A 14OCT 3 MVDASU DK1  0850 1040  14OCT  E  0 CRJ S&#10;     INT"
											value={serviceDetails[serviceId]?.gdsFormat || ''}
											onChange={(e) => updateServiceDetail(serviceId, 'gdsFormat', e.target.value)}
											disabled={loading}
											sx={{ mt: 1 }}
										/>
									</Box>
								))}
							</Box>
						)}

					<TextField
						label="Observaciones o notas para la solicitud"
						multiline
						rows={4}
						value={observations}
						onChange={(e) => setObservations(e.target.value)}
						disabled={loading}
						fullWidth
					/>

					<FileUploader
						files={attachments}
						onChange={setAttachments}
						accept=".pdf,.jpg,.jpeg,.png"
						maxFiles={10}
						maxSizeMB={10}
						disabled={loading}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancelar
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={loading}
					startIcon={loading && <CircularProgress size={16} />}
				>
					Crear solicitud
				</Button>
			</DialogActions>
		</Dialog>
	);
};
